import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import HoursItinerary from "./itinerary/HoursItinerary";
import OneDayItinerary from "./itinerary/OneDayItinerary";
import MultiDayItinerary from "./itinerary/MultiDayItinerary";

import API_BASE_URL from "../services/apiClient";

const PublicTrip = () => {
  const { token } = useParams();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }

    const fetchTrip = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/trips/share/${token}`
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Trip not found");
        }

        setTrip(data);
      } catch (err) {
        setError("This trip is unavailable or expired.");
      } finally {
        setLoading(false);
      }
    };

    fetchTrip();
  }, [token]);

  const handleDownloadPDF = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="space-y-4 text-center">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-[#5b6f00] rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 text-sm">
            Preparing your travel document…
          </p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-red-500 font-medium">
          {error || "Trip not found"}
        </p>
      </div>
    );
  }

  const cityData = trip.cities?.[0];
  const itineraryDays = cityData?.itineraryDays || [];
  const cityName = cityData?.cityName;
  const startDate = cityData?.startDate;
  const endDate = cityData?.endDate;
  const tripType = trip.tripType || "multi";

  const tripLength = itineraryDays.length;

  return (
    <div className="min-h-screen bg-white text-gray-900 print:bg-white">

      {/* HERO */}
      <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">

        <span className="italic text-4xl text-[#5b6f00]">
          EXPEDITIO
        </span>

        <div className="w-16 h-[1px] bg-gray-300 mx-auto my-8" />

        <h2 className="text-5xl font-bold tracking-tight">
          {cityName}
        </h2>

        <p className="mt-4 text-gray-500 text-lg">
          {tripType === "hours" && "Hourly Travel Plan"}
          {tripType === "day" && "1-Day Curated Itinerary"}
          {tripType === "multi" &&
            `${tripLength}-Day Curated Itinerary`}
        </p>

        {(startDate || endDate) && (
          <p className="mt-2 text-sm text-gray-400">
            {startDate?.slice(0, 10)}
            {endDate && ` → ${endDate.slice(0, 10)}`}
          </p>
        )}

        <div className="mt-10 print:hidden">
          <button
            onClick={handleDownloadPDF}
            className="px-7 py-2.5 rounded-full border text-sm font-medium transition hover:bg-gray-100 border-[#5b6f00] text-[#5b6f00]"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-4xl mx-auto px-6 pb-24 print:pb-12">

        {tripType === "hours" && (
          <HoursItinerary
            data={{ days: itineraryDays }}
            city={cityName}
            hideHeader
            editable={false}          />
        )}

        {tripType === "day" && (
          <OneDayItinerary
            data={{ days: itineraryDays }}
            city={cityName}
            hideHeader
            editable={false}
          />
        )}

        {tripType === "multi" && (
          <MultiDayItinerary
            data={{ days: itineraryDays }}
            city={cityName}
            hideHeader
            editable={false}
          />
        )}

      </div>

      <div className="text-center text-xs text-gray-400 pb-12">
        Crafted by Expeditio AI • Smart Travel Planning
      </div>

    </div>
  );
};

export default PublicTrip;